/* ============================================================
   SAM-198 — full-page business view (/business/:id), replacing the list + side
   panel split. Approved mock: "Editor de servicios · pantalla completa", screens
   8 (desktop) and 8m (phone).

   Tabs: Activity · Services · Team · Settings (Profile · Schedule · Closures & special days (SAM-650) · Advanced
   settings), kept in the URL (?tab=, ?section=) so Back and shared links land on
   the same place. Who sees what:
   - Cap Cana / Xhinola admins (`isAdmin`): back to the list, gateway and delete.
   - A business's own admin: no list to go back to; only their own business.
   New Tailwind component (SAM-175 freeze); the Settings forms are the existing
   BusinessDetailPanel in its 'page' variant.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2 } from 'lucide-react';
import { useBusinessById, useBusinessCategories, useBusinessTypes } from '../../hooks/useBusinessCrud';
import { useBusinessContext } from '../../context/BusinessContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useFdCan } from '../../hooks/useFdCan';
import { S3Image } from '../../components/S3Image';
import { BusinessTeamList } from '../../components/BusinessTeamList';
import { BusinessDetailPanel, type BusinessSettingsSection } from '../BusinessDetailPanel';
import { Services } from '../Services';
import { BusinessActivity } from './BusinessActivity';
import { BusinessTeamMember } from './BusinessTeamMember';
import { BusinessCalendarEditor } from './BusinessCalendarEditor';
import type { SAMUser } from '../../types/role.types';
import { useT, type MessageKey } from '../../i18n/admin';
import { ReadOnlyFence } from '../../components/ReadOnlyFence';
import { Modal } from '../../components/ui/Modal';
import { HeaderSaveContext, HeaderSaveStatus, type PublishedHeaderSave } from './headerSave';

type Tab = 'activity' | 'services' | 'team' | 'settings';

const TABS: Array<{ k: Tab; label: MessageKey }> = [
  { k: 'activity', label: 'business.tab.activity' },
  { k: 'services', label: 'business.tab.services' },
  { k: 'team', label: 'business.tab.team' },
  { k: 'settings', label: 'business.tab.settings' },
];

// SAM-650: 'closures' is this page's own section (BusinessCalendarEditor), not a
// BusinessDetailPanel one.
type SettingsSection = BusinessSettingsSection | 'closures';

const SECTIONS: Array<{ k: SettingsSection; label: MessageKey }> = [
  { k: 'profile', label: 'business.section.profile' },
  { k: 'schedule', label: 'business.section.schedule' },
  { k: 'closures', label: 'business.section.closures' },
  { k: 'advanced', label: 'business.section.advanced' },
];

const isTab = (v: string | null): v is Tab => TABS.some((t) => t.k === v);
const isSection = (v: string | null): v is SettingsSection => SECTIONS.some((s) => s.k === v);

export const BusinessPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const isCreate = id === 'new';
  const navigate = useNavigate();
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const { business: contextBusiness } = useBusinessContext();
  const permissions = useUserPermissions();
  const isAdmin = permissions.isAdmin;

  const { data: rows, isLoading } = useBusinessById(isCreate ? null : id);
  const business = rows?.[0] ?? null;
  const { data: categories = [] } = useBusinessCategories();
  const { data: businessTypes = [] } = useBusinessTypes();

  // SAM-1312 (U19): the estate group roles have no Team in their matrix, so the tab is not offered (nor opened by URL).
  // SAM-1321 (U43): the business Operator (a business_invitation account with no other capability) works the passes of
  // its business: Team answers 403 «Not allowed to manage users» and Settings is not its to change, so neither is offered.
  const operatorOnly = Boolean(permissions.isInvitationOnly && !isAdmin);
  const canSeeTeam = useFdCan('team', 'view', !operatorOnly);
  const canSeeSettings = useFdCan('business', 'view', !operatorOnly);
  const tabs = TABS.filter((tb) => (tb.k !== 'team' || canSeeTeam) && (tb.k !== 'settings' || canSeeSettings));
  const tab: Tab = isCreate ? 'settings' : isTab(params.get('tab')) && tabs.some((tb) => tb.k === params.get('tab')) ? (params.get('tab') as Tab) : 'activity';
  const section: SettingsSection = isCreate ? 'profile' : isSection(params.get('section')) ? (params.get('section') as SettingsSection) : 'profile';
  // SAM-1167: the Settings form being edited publishes its save state here; the header draws it.
  const [headerSave, setHeaderSave] = useState<PublishedHeaderSave | null>(null);
  const headerSaveCtx = useMemo(() => ({ publish: setHeaderSave }), []);
  const hasUnsaved = !!headerSave && headerSave.state.mode === 'edit' && headerSave.state.visible;
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  // Runs `action` now, or after «Leave without saving?» when it would drop unsaved changes.
  const guardUnsaved = useCallback((action: () => void) => {
    if (hasUnsaved) setPendingLeave(() => action);
    else action();
  }, [hasUnsaved]);
  // A hard refresh or tab close. The app uses a plain <BrowserRouter> (no useBlocker), so a click
  // on an unrelated sidebar link is not intercepted — the same known gap as the service editor.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  const go = (next: { tab?: Tab; section?: SettingsSection }) => {
    const apply = () => {
      const p = new URLSearchParams(params);
      if (next.tab) p.set('tab', next.tab);
      if (next.section) p.set('section', next.section);
      if (next.tab && next.tab !== 'settings') p.delete('section');
      setParams(p, { replace: true });
    };
    // Profile, Schedule and Advanced share one form, so moving between them keeps the changes; leaving
    // Settings, or going to / from Closures (its own editor), drops them.
    const leavesEditor = (next.tab && next.tab !== 'settings')
      || (next.section !== undefined && (next.section === 'closures') !== (section === 'closures'));
    if (leavesEditor) guardUnsaved(apply); else apply();
  };

  const [member, setMember] = useState<SAMUser | null>(null);
  useEffect(() => { if (tab !== 'team') setMember(null); }, [tab]);

  const category = useMemo(() => {
    const c = categories.find((x) => x.id === business?.business_category_id)?.name;
    const t = businessTypes.find((x) => x.id === (business as { business_type_id?: number } | null)?.business_type_id)?.name;
    return [c, t].filter(Boolean).join(' · ');
  }, [categories, businessTypes, business]);

  // Wait for the Cognito groups: isAdmin is false until they load, and redirecting
  // before that would bounce an admin to their own business.
  if (permissions.isLoading) {
    return <div className="h-full flex items-center justify-center text-sm text-[var(--ink-500)]">{t('common.loading')}</div>;
  }
  // A business's own admin only ever sees their own business.
  if (!isAdmin && isCreate) return <Navigate to="/business/list" replace />;
  if (!isAdmin && contextBusiness?.id && id !== String(contextBusiness.id)) {
    return <Navigate to={`/business/${contextBusiness.id}`} replace />;
  }

  if (!isCreate && isLoading) {
    return <div className="h-full flex items-center justify-center text-sm text-[var(--ink-500)]">{t('business.loading')}</div>;
  }
  if (!isCreate && !business) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-[var(--ink-500)]">
        {t('business.notFound')}
        {isAdmin && (
          <button type="button" className="bz-secondary-btn" onClick={() => navigate('/business/list')}>{t('business.backToList')}</button>
        )}
      </div>
    );
  }

  const name = isCreate ? t('business.new') : business?.name || t('business.fallbackName');

  return (
    <HeaderSaveContext.Provider value={headerSaveCtx}>
    <div className="h-full min-h-0 min-w-0 w-full flex flex-col bg-[var(--surface-2,#f5f5f5)]">
      <header className="shrink-0 bg-white border-b border-[var(--line)]">
        <div className="flex items-center gap-3 px-4 md:px-6 py-3 md:py-4">
          {(isAdmin || permissions.isTenantFrontDesk) && (
            <button
              type="button"
              onClick={() => guardUnsaved(() => navigate('/business/list'))}
              aria-label={t('business.backToList')}
              className="w-10 h-10 shrink-0 rounded-[10px] border border-solid border-[var(--line)] bg-white flex items-center justify-center cursor-pointer"
            >
              <ArrowLeft size={17} />
            </button>
          )}
          <span className="w-10 h-10 md:w-11 md:h-11 shrink-0 rounded-xl bg-[var(--tenant-primary,#005488)] text-white flex items-center justify-center overflow-hidden">
            {business?.logo_url ? (
              <S3Image imageKey={business.logo_url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : <Building2 size={20} />}
          </span>
          <div className="min-w-0 flex flex-col gap-1">
            <h1 className="m-0 text-lg md:text-xl font-bold text-[var(--ink-900)] truncate">{name}</h1>
            {!isCreate && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold">
                {category && <span className="px-2 py-0.5 rounded-full bg-[var(--line-subtle)] text-[var(--ink-700)]">{category}</span>}
                <span className={`px-2 py-0.5 rounded-full ${business?.is_active ? 'bg-[var(--ok-bg)] text-[var(--ok-fg)]' : 'bg-[var(--line-subtle)] text-[var(--ink-500)]'}`}>
                  {business?.is_active ? t('common.status.active') : t('common.status.inactive')}
                </span>
              </div>
            )}
          </div>
          {isCreate && <div className="ml-auto"><HeaderSaveStatus value={headerSave} /></div>}
        </div>
        {!isCreate && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between md:pr-6">
          <nav aria-label={t('business.tabsLabel')} className={`grid ${tabs.length === 4 ? 'grid-cols-4' : tabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} md:flex md:gap-1 px-0 md:px-6`}>
            {tabs.map((tb) => {
              const on = tab === tb.k;
              return (
                <button
                  key={tb.k}
                  type="button"
                  aria-current={on ? 'page' : undefined}
                  onClick={() => go({ tab: tb.k })}
                  className={`h-11 px-4 text-sm font-semibold bg-transparent cursor-pointer border-0 border-b-2 border-solid ${on
                    ? 'border-[var(--tenant-primary,#005488)] text-[var(--ink-900)]'
                    : 'border-transparent text-[var(--ink-500)]'}`}
                >
                  {t(tb.label)}
                </button>
              );
            })}
          </nav>
          <HeaderSaveStatus value={headerSave} />
          </div>
        )}
      </header>

      <div className="flex-1 min-h-0 overflow-auto">
        {tab === 'activity' && business && (
          <div className="p-4 md:p-6"><BusinessActivity businessId={business.id} /></div>
        )}

        {tab === 'services' && business && (
          <div className="h-full min-h-[480px]"><Services businessId={business.id} embedded /></div>
        )}

        {tab === 'team' && business && (
          <div className="p-4 md:p-6">
            {member ? (
              <BusinessTeamMember member={member} businessName={business.name} onBack={() => setMember(null)} />
            ) : (
              <BusinessTeamList businessId={business.id} onOpen={setMember} />
            )}
          </div>
        )}

        {tab === 'settings' && (
          <div className="flex flex-col md:flex-row md:h-full min-h-0">
            {!isCreate && (
              <nav
                aria-label={t('business.sectionsLabel')}
                className="shrink-0 flex md:flex-col gap-1 p-3 md:p-6 md:pr-3 md:w-56 overflow-x-auto"
              >
                {SECTIONS.map((s) => {
                  const on = section === s.k;
                  return (
                    <button
                      key={s.k}
                      type="button"
                      aria-current={on ? 'page' : undefined}
                      onClick={() => go({ section: s.k })}
                      className={`h-10 px-3 rounded-lg text-sm font-semibold text-left whitespace-nowrap border-0 cursor-pointer ${on
                        ? 'bg-[var(--line-subtle)] text-[var(--tenant-primary,#005488)]'
                        : 'bg-transparent text-[var(--ink-700)]'}`}
                    >
                      {t(s.label)}
                    </button>
                  );
                })}
              </nav>
            )}
            <div className="flex-1 min-w-0 min-h-0">
              <ReadOnlyFence section="business">
              {section === 'closures' && business ? (
                <BusinessCalendarEditor key={business.id} business={business} />
              ) : (
                <BusinessDetailPanel
                  key={business?.id ?? 'new'}
                  business={business}
                  mode={isCreate ? 'create' : 'edit'}
                  isAdmin={isAdmin}
                  variant="page"
                  section={section === 'closures' ? 'profile' : section}
                  onClose={() => navigate('/business/list')}
                />
              )}
              </ReadOnlyFence>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={pendingLeave !== null}
        onClose={() => setPendingLeave(null)}
        title={t('business.leave.title')}
        hideClose
        footer={(
          <>
            <button
              type="button"
              className="btn ghost"
              style={{ color: 'var(--danger)' }}
              onClick={() => { const action = pendingLeave; setPendingLeave(null); action?.(); }}
            >
              {t('business.leave.leave')}
            </button>
            <button type="button" className="btn primary" onClick={() => setPendingLeave(null)}>{t('business.leave.stay')}</button>
          </>
        )}
      >
        <p className="text-[14px]" style={{ color: 'var(--ink-700)' }}>{t(tab === 'settings' && section === 'schedule' ? 'business.leave.bodyHours' : 'business.leave.body')}</p>
      </Modal>
    </div>
    </HeaderSaveContext.Provider>
  );
};
